import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import { API_URL } from "../config/api";
import { reportQuestWordCounts } from "../utils/questProgressReporter";
import { pickTitleWordCounts } from "../utils/titleWordCounts";
import "./DeleteWordModal.css";

function normalizeWord(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("fr-FR");
}

export default function DeleteWordModal({ word, open, onClose, onDeleted }) {
  const [typedWord, setTypedWord] = useState("");
  const [phase, setPhase] = useState("confirm");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const onCloseRef = useRef(onClose);
  const onDeletedRef = useRef(onDeleted);
  const finishedRef = useRef(false);

  onCloseRef.current = onClose;
  onDeletedRef.current = onDeleted;

  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onDeletedRef.current?.();
    onCloseRef.current?.();
  };

  useEffect(() => {
    if (!open) {
      finishedRef.current = false;
      return;
    }
    setTypedWord("");
    setPhase("confirm");
    setSubmitting(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (!open || phase !== "success") return undefined;

    const timer = window.setTimeout(() => {
      finish();
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [open, phase]);

  if (!open || !word) return null;

  const wordMatches = normalizeWord(typedWord) === normalizeWord(word);

  const handleClose = () => {
    if (submitting) return;
    if (phase === "success") {
      finish();
      return;
    }
    onClose();
  };

  const handleConfirm = async () => {
    if (!wordMatches || submitting || phase !== "confirm") return;

    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/mots/${encodeURIComponent(word)}`, {
        method: "DELETE",
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.message || "Impossible de supprimer ce mot.");
      }

      reportQuestWordCounts(pickTitleWordCounts(result));
      setPhase("success");
    } catch (err) {
      setError(err.message || "Erreur lors de la suppression.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        className="delete-word-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-word-modal-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={handleClose}
          disabled={submitting}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        {phase === "confirm" ? (
          <>
            <h3 id="delete-word-modal-title" className="delete-word-modal__title">
              Supprimer ce mot ?
            </h3>
            <p className="delete-word-modal__text">
              Cette action est définitive. Pour confirmer, tapez exactement le mot{" "}
              <strong className="delete-word-modal__word">{word}</strong>.
            </p>

            <input
              type="text"
              className="delete-word-modal__input"
              value={typedWord}
              onChange={(event) => setTypedWord(event.target.value)}
              placeholder="Tapez le mot ici…"
              autoFocus
              disabled={submitting}
              onKeyDown={(event) => {
                if (event.key === "Enter" && wordMatches && !submitting) {
                  handleConfirm();
                }
              }}
            />

            {error && <p className="delete-word-modal__error">{error}</p>}

            <div className="delete-word-modal__actions">
              <button
                type="button"
                className="delete-word-modal__btn delete-word-modal__btn--cancel"
                onClick={handleClose}
                disabled={submitting}
              >
                Annuler
              </button>
              <button
                type="button"
                className="delete-word-modal__btn delete-word-modal__btn--confirm"
                onClick={handleConfirm}
                disabled={!wordMatches || submitting}
              >
                {submitting ? "Suppression…" : "Valider"}
              </button>
            </div>
          </>
        ) : (
          <p className="delete-word-modal__success">
            Le mot <strong>{word}</strong> a été supprimé du registre.
          </p>
        )}
      </div>
    </div>,
    getAppPortalRoot()
  );
}
